import { type Ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { useStyleTag } from '@vueuse/core'
import { useIsIOS } from '@/plugins/is-ios.ts'
import { useWindowScroll } from '@/composables/vueuse/use-window-scroll.ts'

type HideBodyOverflowManual = {
    isHidden: Readonly<Ref<boolean>>
}

export function useHideBodyOverflow(manual?: HideBodyOverflowManual): void {
    const { y } = useWindowScroll()
    let ySaved = y.value

    const { loadStyle, unloadStyle, isLoaded } =
        useHideBodyOverflowStyleTag(!manual)

    const hide = () => {
        ySaved = y.value

        loadStyle()
    }

    const unhide = () => {
        // This check prevents multiple calls which could interfere with
        // routing scroll restoration
        if (!isLoaded.value) {
            return
        }

        unloadStyle()

        y.value = ySaved
    }

    if (manual) {
        watch(manual.isHidden, (newIsHidden) => {
            if (newIsHidden) {
                hide()
            } else {
                unhide()
            }
        })
    }

    onMounted(() => {
        if (!manual) {
            hide()
        }
    })
    // onDeactivated(unhide)
    onBeforeUnmount(unhide)
}

function useHideBodyOverflowStyleTag(immediate: boolean) {
    const isIOS = useIsIOS()

    // iOS hack
    const iOsStyle = isIOS
        ? 'position: fixed; left: 0; right: 0; height: 0'
        : ''

    const {
        load: loadStyle,
        unload: unloadStyle,
        isLoaded,
    } = useStyleTag(`body { overflow: hidden; ${iOsStyle} }`, { immediate })

    return {
        loadStyle,
        unloadStyle,
        isLoaded,
    }
}
